import { roundNumber } from "src/utils/common-utils";
import { checkQuestionIsSelect } from ".";
import { ESG_ROW_KEYS } from "../constants";
import {
    calculateAdminTotals,
    calculateExternalTotals,
    calculateFinalScore,
    calculateFinalTotals,
    calculateSelfTotals,
    convertAdminScore,
    getAverageExternalScore,
    getMaxPossibleScore,
    getNumberExternalAnswers,
    getRomanNumeral
} from "./assessment-utils";
import { upperFirst } from "lodash";

function removeTagsFromLabel(label) {
    return label?.replace(/<[^>]*>?/gm, '') || "";
}


export const createEsgCalculationBreakdownRows = ({ surveyAreas, internalSurveyResponses, externalSurveyResponses, adminSurveyResponses }) => {
    const rows = [];
    const areaRows = []
    surveyAreas.forEach((area, areaIdx) => {
        area.questions.forEach((question, questionIdx) => {
            const internalScore =
                internalSurveyResponses?.[area.key]?.[question.key]?.score || 0;

            const adminScore = convertAdminScore(
                adminSurveyResponses?.[area.key]?.[question.key]?.score,
                internalScore
            );

            const weight = question.weight;

            const externalScore = getAverageExternalScore(
                externalSurveyResponses,
                area.key,
                question.key
            );

            const finalScore = calculateFinalScore(adminScore, externalScore);

            const isSelect = checkQuestionIsSelect(question);

            const maxPossibleScore = isSelect ? getMaxPossibleScore(question) : 4

            const numberExternalAnswers = getNumberExternalAnswers(
                externalSurveyResponses,
                area.key,
                question.key
            );


            const percentage = roundNumber(finalScore / maxPossibleScore * 100, 2)



            const questionRow = {
                [ESG_ROW_KEYS.idx]: questionIdx + 1 + ".",
                [ESG_ROW_KEYS.question]: removeTagsFromLabel(question.label),
                [ESG_ROW_KEYS.maxPossibleScore]: maxPossibleScore,
                "weighted-score": +finalScore * weight || 0,
                "max-weighted-score": maxPossibleScore * weight || 0,
                [ESG_ROW_KEYS.selfValue]: +internalScore || 0,
                [ESG_ROW_KEYS.adminScore]: +adminScore || 0,
                [ESG_ROW_KEYS.numExternalAnswers]: numberExternalAnswers,
                [ESG_ROW_KEYS.externalAverage]: +externalScore || 0,
                [ESG_ROW_KEYS.finalScore]: +finalScore || 0,
                [ESG_ROW_KEYS.weight]: +weight || "",
                [ESG_ROW_KEYS.area]: area.label,
                type: question.type || "select",
                percentage
            }

            rows.push(questionRow);
        })



        const totalWeightedScore = rows.filter(r => r.area === area.label).reduce((acc, curr) => acc + (curr["weighted-score"] || 0), 0)
        const totalMaxWeightedScore = rows.filter(r => r.area === area.label).reduce((acc, curr) => acc + (curr["max-weighted-score"] || 0), 0)

        const totalPercentage = roundNumber(totalWeightedScore / totalMaxWeightedScore * 100, 2)
        const areaTotalRow = {
            [ESG_ROW_KEYS.idx]: getRomanNumeral(areaIdx + 1),
            [ESG_ROW_KEYS.area]: "Totals",
            [ESG_ROW_KEYS.question]: area.label,
            "weighted-score": totalWeightedScore,
            "max-weighted-score": totalMaxWeightedScore,
            "percentage": totalPercentage,
        };

        rows.push({})
        areaRows.push(areaTotalRow);

    })

    const totalWeightedScore = rows.reduce((acc, curr) => acc + (curr["weighted-score"] || 0), 0)
    const totalMaxWeightedScore = rows.reduce((acc, curr) => acc + (curr["max-weighted-score"] || 0), 0)

    const totalPercentage = roundNumber(totalWeightedScore / totalMaxWeightedScore * 100, 2)
    const totalRow = {
        [ESG_ROW_KEYS.area]: "Totals",
        [ESG_ROW_KEYS.question]: "Final",
        "weighted-score": totalWeightedScore,
        "max-weighted-score": totalMaxWeightedScore,
        "percentage": totalPercentage,
    }
    rows.push({})
    rows.push(...areaRows)
    rows.push(totalRow);
    return rows

}

export function createEsgSummaryRows({
    surveyAreas,
    internalSurveyResponses,
    externalSurveyResponses,
    adminSurveyResponses }) {

    const rows = [];
    const areas = [];

    surveyAreas.forEach((area, areaIdx) => {

        // Question rows
        area.questions.forEach((question, questionIdx) => {

            const isAnswered = internalSurveyResponses?.[area.key]?.[question.key]?.score !== undefined

            const internalScore =
                internalSurveyResponses?.[area.key]?.[question.key]?.score || 0;

            const internalAnswerLabel =
                question.answers.find((a) => a.value == internalScore)?.label || "";

            const modifyAnswerText = (score, label, weight) => {
                if (+weight === 0) {
                    return label
                }
                let indexToInsert = label.indexOf("</strong>");
                if (indexToInsert === -1) {
                    indexToInsert = label.indexOf("</p>");
                }
                const firstPart = label.slice(0, indexToInsert + 9);
                const secondPart = label.slice(indexToInsert + 9);

                const points = score == 1 ? "point" : "points";
                return `${firstPart} <span>(${score} ${points})</span> ${secondPart}`;
            }

            const internalAnswerFullText =
                internalScore !== undefined && internalScore !== null
                    ? modifyAnswerText(internalScore, internalAnswerLabel, question.weight)
                    : "";

            const numberExternalAnswers = getNumberExternalAnswers(
                externalSurveyResponses,
                area.key,
                question.key
            );

            const adminScore = convertAdminScore(
                adminSurveyResponses?.[area.key]?.[question.key]?.score,
                internalScore,
                question.weight
            );

            const adminComment = adminSurveyResponses?.[area.key]?.[question.key]?.adminComment

            const externalScore = getAverageExternalScore(
                externalSurveyResponses,
                area.key,
                question.key
            );

            const finalScore = calculateFinalScore(adminScore, externalScore);

            const maxPossibleScore = getMaxPossibleScore(question);

            // for the "What's next?" column
            const nextAnswerLabel =
                question.answers.find((a) => a.value > finalScore)?.label || "";


            if (!checkQuestionIsSelect(question)) {

                const response = internalSurveyResponses[area.key]?.[question.key]
                let answer = response?.answer || ""
                if (question.type === "date-range" && answer) {
                    answer = `${answer.from} - ${answer.to}`
                }

                const questionRow = {
                    [ESG_ROW_KEYS.idx]: questionIdx + 1 + ".",
                    [ESG_ROW_KEYS.question]: question.label,
                    [ESG_ROW_KEYS.maxPossibleScore]: 4,
                    [ESG_ROW_KEYS.selfValue]: 0,
                    [ESG_ROW_KEYS.selfScore]: "",
                    [ESG_ROW_KEYS.adminScore]: adminScore,
                    [ESG_ROW_KEYS.numExternalAnswers]: numberExternalAnswers,
                    [ESG_ROW_KEYS.externalAverage]: 0,
                    [ESG_ROW_KEYS.finalScore]: finalScore,
                    [ESG_ROW_KEYS.weight]: question.weight,
                    [ESG_ROW_KEYS.area]: area.label,
                    [ESG_ROW_KEYS.nextAnswerLabel]: "",
                    key: question.key,
                    answer,
                    adminComment,
                    type: question.type,
                    isAnswered,
                    _classes: `table-question row-${areaIdx}`,
                }

                rows.push(questionRow);

                return;
            }


            const questionRow = {
                [ESG_ROW_KEYS.idx]: questionIdx + 1 + ".",
                [ESG_ROW_KEYS.question]: question.label,
                [ESG_ROW_KEYS.maxPossibleScore]: maxPossibleScore,
                [ESG_ROW_KEYS.selfValue]: internalScore,
                [ESG_ROW_KEYS.selfScore]: internalAnswerFullText,
                [ESG_ROW_KEYS.adminScore]: adminScore,
                [ESG_ROW_KEYS.numExternalAnswers]: numberExternalAnswers,
                [ESG_ROW_KEYS.externalAverage]: externalScore,
                [ESG_ROW_KEYS.finalScore]: finalScore,
                [ESG_ROW_KEYS.weight]: question.weight,
                [ESG_ROW_KEYS.area]: area.label,
                [ESG_ROW_KEYS.nextAnswerLabel]: nextAnswerLabel,
                key: question.key,
                adminComment,
                type: "select",
                isAnswered,
                _classes: `table-question row-${areaIdx}`,
            }

            rows.push(questionRow);
        });

        // Area total row
        const {
            totalScore: finalScore,
            totalPercentage: finalPercentage,
            totalPossibleScore

        } = calculateFinalTotals(rows, area.label);


        const { totalText: adminTotalText } = calculateAdminTotals(rows, area.label)

        const { totalText: externalTotalText } = calculateExternalTotals(rows, area.label)

        const areaTotalRow = {
            [ESG_ROW_KEYS.idx]: getRomanNumeral(areaIdx + 1),
            [ESG_ROW_KEYS.question]: area.label,
            [ESG_ROW_KEYS.selfScore]: "",
            [ESG_ROW_KEYS.adminScore]: adminTotalText,
            [ESG_ROW_KEYS.numExternalAnswers]: "",
            [ESG_ROW_KEYS.externalAverage]: externalTotalText,
            [ESG_ROW_KEYS.finalScore]: finalPercentage + "%",
            [ESG_ROW_KEYS.weight]: "",
            _classes: `table-sector row-${areaIdx}`,
        };

        //insert area total row before the first question in the area
        const firstRowInAreaIdx = rows.findIndex((r) => r.area === area.label);
        if (firstRowInAreaIdx !== -1) {
            rows.splice(firstRowInAreaIdx, 0, areaTotalRow);
        }

        areas.push({
            label: area.label,
            score: finalScore,
            percentage: finalPercentage,
            total: totalPossibleScore,
        });
    });

    // Total row
    const {
        totalPercentage: finalPercentage,
        totalScore: finalScore,
        totalPossible,
    } = calculateFinalTotals(rows);

    const totalRow = {
        [ESG_ROW_KEYS.idx]: "",
        [ESG_ROW_KEYS.question]: "Total",
        [ESG_ROW_KEYS.selfScore]: calculateSelfTotals(rows).totalPercentage + "%",
        [ESG_ROW_KEYS.adminScore]: calculateAdminTotals(rows).totalText,
        [ESG_ROW_KEYS.numExternalAnswers]: "",
        [ESG_ROW_KEYS.externalAverage]: calculateExternalTotals(rows).totalText,
        [ESG_ROW_KEYS.finalScore]: finalPercentage + "%",
        [ESG_ROW_KEYS.weight]: "",
        _classes: "table-total",
    }


    rows.push(totalRow);


    return { rows, areas, finalPercentage, finalScore, totalPossible };

}

export function createEsgExternalRows({ surveyAreas,
    externalSurveyResponses,
    orgName }) {

    const rows = [];
    surveyAreas.forEach((area, idx) => {

        const areaTotalRow = {
            idx: getRomanNumeral(idx + 1),
            question: area.label,
            "external-answers": "",
            "external-average": "",
            _classes: "table-sector",
            weight: 0,
        };

        rows.push(areaTotalRow);

        area.questions.forEach((question, idx) => {
            const numAnswers = getNumberExternalAnswers(
                externalSurveyResponses,
                area.key,
                question.key
            );

            const maxScore = getMaxPossibleScore(question);


            const questionRow = {
                idx: idx + 1 + ".",
                question:
                    question.labelExternal?.replaceAll("{ORG_NAME}", orgName) || "",
                weight: question.weight,
                "external-answers": numAnswers,
                maxScore,
                "external-average": getAverageExternalScore(
                    externalSurveyResponses,
                    area.key,
                    question.key
                ),
            }
            rows.push(questionRow);
        });
    });
    const totalRow = {
        idx: "",
        question: "Total",
        "external-answers": "",
        "external-average": calculateExternalTotals(rows).totalText,
        _classes: "table-total",
    };

    rows.push(totalRow);

    return rows;
}

export function createEsgEditRows({ surveyAreas }) {
    const rows = [];
    surveyAreas?.forEach((area, areaIdx) => {
        rows.push({
            idx: getRomanNumeral(areaIdx + 1),
            question: area.label,
            weight: "",
            _classes: "table-sector",
            edit: "",
        });

        area.questions.forEach((question, questionIdx) => {
            rows.push({
                idx: questionIdx + 1,
                question: question.label,
                weight: question.weight,
                edit: true,
                areaIdx,
                questionIdx,
                questionName: `areas.${areaIdx}.questions.${questionIdx}`,
            });
        });
    });
    return rows;
}

export function createAdminCommentRows({ surveyAreas, adminSurveyResponses }) {
    const rows = [];
    surveyAreas.forEach((area) => {
        area.questions.forEach((question) => {
            const adminComment = adminSurveyResponses?.[area.key]?.[question.key]?.adminComment || "";
            rows.push({
                area: upperFirst(area.key),
                question: removeTagsFromLabel(question.label),
                adminComment,
            });
        });
    });
    return rows;
}


export function createEsgInternalRows({ orgId, surveyAreas, internalSurveyResponses, adminSurveyResponses, isStandalone }) {
    const rows = [];
    surveyAreas.forEach((area, idx) => {
        rows.push({
            idx: getRomanNumeral(idx + 1),
            question: area.label,
            "self-score": "",
            "admin-score": "n/a",
            _classes: "table-sector",
        });

        area.questions.forEach((question, idx) => {

            const storagePath = isStandalone ? `survey/standalone/${orgId}/${area.key}/${question.key}` : `survey/${orgId}/${area.key}/${question.key}`
            const answerEvidence =
                internalSurveyResponses[area.key]?.[question.key]?.evidence || "";

            const answerValue = internalSurveyResponses[area.key]?.[question.key]?.score || 0;

            const answerLabel =
                question?.answers?.find((a) => a.value === +answerValue)?.label || "";

            const modifyAnswerText = (score, label, weight) => {
                if (+weight === 0) {
                    return label
                }
                let indexToInsert = label.indexOf("</strong>");
                if (indexToInsert === -1) {
                    indexToInsert = label.indexOf("</p>");
                }
                const firstPart = label.slice(0, indexToInsert + 9);
                const secondPart = label.slice(indexToInsert + 9);

                const points = score == 1 ? "point" : "points";
                return `${firstPart} <span>(${score} ${points})</span> ${secondPart}`;
            }

            const scoreString =
                answerValue !== undefined && answerValue !== null
                    ? modifyAnswerText(answerValue, answerLabel, question.weight)
                    : "";

            const adminScore = convertAdminScore(
                adminSurveyResponses?.[area.key]?.[question.key]?.score
            );
            const adminComment = adminSurveyResponses?.[area.key]?.[question.key]?.adminComment

            let maxScore = 0;
            question.answers.forEach((a) => {
                if (a.value > maxScore) {
                    maxScore = a.value;
                }
            });



            if (!checkQuestionIsSelect(question)) {
                const response = internalSurveyResponses[area.key]?.[question.key]
                let answer = response?.answer || ""
                if (question.type === "date-range" && answer) {
                    answer = `${answer.from} - ${answer.to}`
                }

                rows.push({
                    idx: idx + 1,
                    weight: question.weight,
                    question: question.label,
                    area: area.key,
                    key: question.key,
                    "self-score": 0,
                    "admin-score": adminScore,
                    "self-value": 0,
                    maxScore: 4,
                    evidence: answerEvidence,
                    storagePath,
                    adminComment,
                    answer: answer,
                    type: question.type
                });
                return;
            }


            rows.push({
                idx: idx + 1,
                weight: question.weight,
                question: question.label,
                area: area.key,
                key: question.key,
                "self-score": scoreString,
                "admin-score": adminScore,
                "self-value": answerValue,
                maxScore,
                evidence: answerEvidence,
                adminComment,
                storagePath,
                type: "select"
            });
        });
    });

    rows.push({
        idx: "",
        question: "Total",
        "self-score": calculateSelfTotals(rows).totalText,
        "admin-score": calculateAdminTotals(rows).totalText,
        _classes: "table-total",
    });

    return rows;
}